import { useMemo, useState } from 'react';
import { AdvancedFilterButton, FilterPanelCollapse } from '@/components/common/AdvancedFilter';
import { DataTable } from '@/components/common/DataTable';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { ErrorState } from '@/components/common/ErrorState';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { StatCardGrid } from '@/components/common/StatCardGrid';
import { TAKA_SYMBOL } from '@/constants/app.constants';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { useRevealLastViewedRow } from '@/hooks/useLastViewedRow';
import { zonalStatementInfiniteQueryOptions } from '../api/zonalStatement.api';
import { ZonalStatementFilterPanel } from '../components/ZonalStatementFilterPanel';
import { getZonalStatementColumns } from '../components/ZonalStatementColumns';
import { useZonalStatementFilters } from '../hooks/useZonalStatementFilters';

export function ZonalStatementPage() {
  const controller = useZonalStatementFilters();
  const { rows, firstPageMeta, isLoading, isRefetching, loadError, refetch, loadMoreProps } =
    useInfiniteTableQuery(zonalStatementInfiniteQueryOptions(controller.params));

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const lastViewedUserId = useRevealLastViewedRow('zonalStatementZms');

  const columns = useMemo(() => getZonalStatementColumns(controller.params), [controller.params]);

  const totals = firstPageMeta?.totals;
  const { dateFrom, dateTo } = controller.params;

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title="Zonal Statement"
        titleAddon={<DateRangeBadge dateFrom={dateFrom} dateTo={dateTo} />}
        actions={
          <AdvancedFilterButton
            isOpen={isFilterOpen}
            activeCount={controller.activeFilterCount}
            onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
          />
        }
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <ZonalStatementFilterPanel controller={controller} />
      </FilterPanelCollapse>

      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the zonal statement"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <>
          <StatCardGrid
            isLoading={isLoading}
            isFetching={isRefetching}
            stats={[
              {
                key: 'zms',
                label: 'ZMs',
                value: firstPageMeta?.total,
              },
              {
                key: 'dcrAmount',
                label: 'DCR',
                value: totals?.dcrAmount,
                unit: TAKA_SYMBOL,
              },
              {
                key: 'derAmount',
                label: 'DER',
                value: totals?.derAmount,
                unit: TAKA_SYMBOL,
              },
              {
                key: 'specimenQty',
                label: 'Specimen Qty',
                value: totals?.specimenQty,
              },
            ]}
          />

          <DataTable
            data={rows}
            columns={columns}
            isLoading={isLoading}
            className="lg:flex-1"
            stickyColumnId="name"
            emptyMessage="Nothing is waiting to be published"
            emptyHint="Try a wider date range."
            isRowHighlighted={(member) => member.userId === lastViewedUserId}
            {...loadMoreProps}
          />
        </>
      )}
    </div>
  );
}
